import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_IMAGE = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=1080&fit=crop";
const LONDON_IMAGE = "https://images.unsplash.com/photo-1675982828880-13296f215000?w=800&h=600&fit=crop";
const MAYFAIR_IMAGE = "https://images.unsplash.com/photo-1692820338246-11cb8571f696?w=800&h=600&fit=crop";

// Minimal text logo component - Exor/LMDV style
function HeroLogo() {
  return (
    <div className="flex flex-col items-center leading-none text-white">
      <span className="text-5xl md:text-7xl font-light tracking-[0.2em]">BNDV</span>
      <span className="text-xs md:text-sm uppercase tracking-[0.5em] opacity-50 mt-2">Capital</span>
    </div>
  );
}

export default function HomePage() {
  const [financials, setFinancials] = useState(null);
  const [news, setNews] = useState([]);
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 400], [1, 1.1]);

  useEffect(() => {
    async function fetchData() {
      try {
        const [finRes, newsRes] = await Promise.all([
          axios.get(`${API}/api/financials`),
          axios.get(`${API}/api/news`)
        ]);
        setFinancials(finRes.data);
        setNews(newsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    }
    fetchData();
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div className="bg-white">
      {/* Hero Section - Full Screen with Parallax */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div 
          className="absolute inset-0"
          style={{ scale: heroScale }}
        >
          <div className="w-full h-full overflow-hidden">
            <img
              src={HERO_IMAGE}
              alt="Corporate"
              className="w-full h-full object-cover animate-ken-burns"
              data-testid="hero-image"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/70 via-[#0A192F]/50 to-[#0A192F]/80" />
        </motion.div>
        
        <motion.div 
          className="relative z-10 max-w-5xl mx-auto px-6 text-center"
          style={{ opacity: heroOpacity }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <HeroLogo />
            </motion.div>
            <motion.p 
              className="text-white/40 text-xs md:text-sm uppercase tracking-[0.4em] mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Single Family Office
            </motion.p>
            <motion.div
              className="w-16 h-px bg-white/30 mx-auto mb-8"
              initial={{ width: 0 }}
              animate={{ width: 64 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            />
            <motion.h1 
              className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white/90 tracking-wide leading-relaxed mb-10 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Diversified Investments.<br />
              Sustainable Value.<br />
              <span className="text-white/60">Global Vision.</span>
            </motion.h1>
            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-[#0A192F] text-xs uppercase tracking-[0.2em] font-medium hover:bg-white/90 transition-all"
              >
                Discover More
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-3 px-8 py-4 border border-white/20 text-white text-xs uppercase tracking-[0.2em] hover:bg-white/5 transition-all"
              >
                Our Portfolio
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.button
          onClick={scrollToContent}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/50 hover:text-white transition-colors"
          aria-label="Scroll down"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <ArrowDown className="w-6 h-6" />
          </motion.div>
        </motion.button>
      </section>

      {/* About Section - Visual Split */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#0A192F]/50 mb-4">Our Approach</p>
              <h2 className="font-serif text-4xl lg:text-5xl text-[#0A192F] tracking-tight mb-6 leading-tight">
                Long-Term Partners,<br />
                Not Short-Term Investors
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                BNDV Capital is a Single Family Office that thinks in generations, not quarters. 
                Through Dolchester Holding, we partner with exceptional management teams to build 
                market-leading positions in attractive European sectors.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our portfolio spans luxury, cosmetics, healthcare, industry, and food & beverage – 
                sectors where we leverage deep expertise and networks to create sustainable value.
              </p>
              <Link
                to="/portfolio"
                className="group inline-flex items-center gap-3 text-[#0A192F] text-sm uppercase tracking-wider font-medium"
                data-testid="home-portfolio-link"
              >
                Explore Our Sectors
                <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <img
                  src={MAYFAIR_IMAGE}
                  alt="London Mayfair"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-8 -left-8 bg-[#0A192F] text-white p-8 max-w-xs">
                  <p className="font-serif text-2xl mb-2">London</p>
                  <p className="text-sm text-white/70">45 Albemarle Street, Mayfair</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Investment Sectors - Visual Grid */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-[#0A192F]/50 mb-4">Dolchester Holding</p>
            <h2 className="font-serif text-4xl text-[#0A192F] tracking-tight">Investment Sectors</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Luxury', icon: '◆' },
              { name: 'Cosmetics', icon: '✧' },
              { name: 'Healthcare', icon: '+' },
              { name: 'Industry', icon: '▣' },
              { name: 'Food', icon: '◎' },
            ].map((sector, index) => (
              <motion.div
                key={sector.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white p-8 border border-slate-200 hover:border-[#0A192F] hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="text-3xl text-[#0A192F]/20 group-hover:text-[#0A192F] transition-colors mb-4">
                  {sector.icon}
                </div>
                <h3 className="font-medium text-[#0A192F]">{sector.name}</h3>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-[#0A192F] text-sm uppercase tracking-wider font-medium hover:gap-4 transition-all"
            >
              View All Sectors
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 bg-[#0A192F] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Global Presence</p>
              <h2 className="font-serif text-4xl tracking-tight mb-8">Five Offices Across Europe & Middle East</h2>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { city: 'London', type: 'Headquarters' },
                  { city: 'Amsterdam', type: 'Office' },
                  { city: 'Milan', type: 'Office' },
                  { city: 'Dubai', type: 'Office' },
                  { city: 'Paris', type: 'Office' },
                ].map((office, index) => (
                  <motion.div
                    key={office.city}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3"
                  >
                    <div className={`w-2 h-2 ${office.type === 'Headquarters' ? 'bg-white' : 'bg-white/50'}`} />
                    <span className={office.type === 'Headquarters' ? 'font-medium' : 'text-white/70'}>
                      {office.city}
                    </span>
                    {office.type === 'Headquarters' && (
                      <span className="text-[10px] uppercase tracking-wider bg-white/20 px-2 py-0.5">HQ</span>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <img
                src={LONDON_IMAGE}
                alt="London"
                className="w-full h-[400px] object-cover opacity-80"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#0A192F]/50 mb-4">Latest Updates</p>
              <h2 className="font-serif text-4xl text-[#0A192F] tracking-tight">News & Press</h2>
            </motion.div>
            <Link
              to="/news"
              className="hidden sm:inline-flex items-center gap-2 text-[#0A192F] text-sm uppercase tracking-wider font-medium hover:gap-4 transition-all"
              data-testid="home-all-news-link"
            >
              All News
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {news.map((article, index) => (
              <motion.article
                key={article.article_id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`news-preview-${index}`}
                className="group"
              >
                <Link to={`/news/${article.article_id}`}>
                  {article.image_url && (
                    <div className="overflow-hidden mb-6">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-wider text-[#0A192F]/50 mb-3">
                    {article.category} · {article.date}
                  </div>
                  <h3 className="font-serif text-xl text-[#0A192F] mb-3 group-hover:text-[#0A192F]/70 transition-colors leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </Link>
              </motion.article>
            ))}
          </div>

          <div className="mt-8 sm:hidden text-center">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-[#0A192F] text-sm uppercase tracking-wider font-medium"
            >
              All News
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl lg:text-4xl text-[#0A192F] tracking-tight mb-6">
              Interested in Partnering With Us?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              We welcome conversations with management teams, advisors, and investors 
              who share our vision of long-term value creation.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-10 py-5 bg-[#0A192F] text-white text-sm uppercase tracking-wider font-medium hover:bg-[#112240] transition-colors"
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
